import React, { Fragment, ReactNode } from 'react';

import { useTabs } from '@hooks';
import type { TabContentConfig, TabItem, TabsProps } from '@types';
import { cn } from '@utils';

import { Tabs } from './Tabs';

interface TabContainerProps<T extends string = string> extends Omit<TabsProps, 'tabs' | 'activeTab' | 'onTabChange'> {
  tabs: TabItem[];
  defaultTab?: T;
  onTabChange?: (tabKey: T) => void;
  contentConfig?: TabContentConfig<T>;
  contentClassName?: string;
  adornment?: ReactNode;
  adornmentClassName?: string;
  showContent?: boolean;
  renderCustomContent?: (params: {
    activeTab: T;
    activeTabData: TabItem | undefined;
    renderContent: () => React.ReactNode;
  }) => React.ReactNode;
}

export function TabContainer<T extends string = string>(props: Readonly<TabContainerProps<T>>) {
  const {
    tabs,
    defaultTab,
    onTabChange,
    contentConfig,
    contentClassName = '',
    adornment,
    adornmentClassName = '',
    showContent = true,
    renderCustomContent,
    ...tabsProps
  } = props;

  const { activeTab, handleTabChange, renderContent, activeTabData } = useTabs({
    tabs,
    defaultTab,
    onTabChange,
    contentConfig,
  });

  return (
    <Fragment>
      <div className={cn('flex justify-between sm:flex-row', adornmentClassName)}>
        <Tabs {...tabsProps} tabs={tabs} activeTab={activeTab} onTabChange={handleTabChange} />
        {!!adornment && adornment}
      </div>

      {showContent && (
        <div className={cn('flex flex-col', contentClassName)}>
          {renderCustomContent ? renderCustomContent({ activeTab, activeTabData, renderContent }) : renderContent()}
        </div>
      )}
    </Fragment>
  );
}

TabContainer.displayName = 'TabContainer';
